import {createAuthenticatedApiClient} from './apiClient';
import {authService} from './auth.service';
import {filterUsersByCurrentTenant, getTenantIdFromUser} from '@utils/tenant';
import {normalizeUploadsPath, pickProfilePhotoRaw, profilePhotoSrc} from '@utils/profilePhotoUrl';
import {normalizeSignatureUploadsPath, pickProfileSignatureRaw} from '@utils/profileSignatureUrl';
import {foldForRoleMatch, getRoleIdFromUser, getRoleNameFromUser} from '@utils/roleAccess';

const PROFILES_STORAGE_KEY = 'profiles_users';

const profilesAPI = createAuthenticatedApiClient();

let cachedPhotoBlobUrl = null;
let cachedPhotoBlobPath = null;
let cachedSignatureBlobUrl = null;
let cachedSignatureBlobPath = null;

const roleLabelMap = {
    1: 'super_admin',
    2: 'admin',
    3: 'doctor',
    4: 'user'
};

const roleIdMap = {
    super_admin: 1,
    superadmin: 1,
    admin: 2,
    doctor: 3,
    medico: 3,
    user: 4,
    usuario: 4
};

const roleNamePayloadMap = {
    user: 'Usuario',
    usuario: 'Usuario',
    doctor: 'Medico',
    medico: 'Medico'
};

const normalizeRoleName = rawRole => {
    const roleNameRaw = foldForRoleMatch(rawRole);

    if (roleNameRaw === '1') return 'super_admin';
    if (roleNameRaw === '2') return 'admin';
    if (roleNameRaw === '3') return 'doctor';
    if (roleNameRaw === '4') return 'user';
    if (roleNameRaw.includes('super')) return 'super_admin';
    if (roleNameRaw.includes('admin')) return 'admin';
    if (roleNameRaw.includes('doctor') || roleNameRaw.includes('medico')) return 'doctor';
    return 'user';
};

/** El PUT /usuarios/me a veces no devuelve rol; sin esto normalizeUser cae en "user". */
const hasRoleInApiPayload = payload => {
    const raw = payload?.data ?? payload?.user ?? payload;
    if (!raw || typeof raw !== 'object') {
        return false;
    }

    if (raw.role_id != null || raw.roleId != null || raw.rol_id != null) {
        return true;
    }

    if (raw.role_name || raw.rol_nombre) {
        return true;
    }

    if (raw.role?.nombre || raw.Role?.nombre) {
        return true;
    }

    if (typeof raw.role === 'string' && raw.role.trim()) {
        return true;
    }

    const numericRole = Number(raw.role);
    return Number.isFinite(numericRole) && numericRole >= 1 && numericRole <= 4;
};

const preserveRoleFields = (merged, previous) => {
    if (!previous) {
        return merged;
    }

    const roleId = getRoleIdFromUser(previous) || Number(previous.role_id ?? previous.role) || null;
    const roleName = normalizeRoleName(getRoleNameFromUser(previous) || previous.role_name || previous.rol_nombre);

    return {
        ...merged,
        role_id: roleId || merged.role_id,
        role_name: roleName || merged.role_name,
        rol_nombre: previous.rol_nombre ?? merged.rol_nombre,
        role: previous.role ?? merged.role
    };
};

/** Ruta relativa canónica `/uploads/profile_photos/...` para guardar en sesión. */
const extractProfilePhotoPathFromResponse = data => {
    const raw = data?.foto_perfil_url;

    if (!raw) {
        return null;
    }

    return normalizeUploadsPath(raw) || String(raw).trim().replace(/\\/g, '/') || null;
};

/** Ruta relativa canónica `/uploads/profile_signatures/...` para guardar en sesión. */
const extractProfileSignaturePathFromResponse = data => {
    const raw = data?.firma_url ?? pickProfileSignatureRaw(data);

    if (!raw) {
        return null;
    }

    return normalizeSignatureUploadsPath(raw) || String(raw).trim().replace(/\\/g, '/') || null;
};

const unwrapMeResponse = raw => {
    if (!raw || typeof raw !== 'object') {
        return {};
    }

    if (raw.user && typeof raw.user === 'object') {
        return raw.user;
    }

    return raw;
};

const throwApiError = (error, fallbackMessage) => {
    const message = error?.response?.data?.message || error?.response?.data?.error;

    throw new Error(message || fallbackMessage);
};

const normalizeUser = user => {
    const roleName = normalizeRoleName(
        user?.role_name ||
        user?.rol_nombre ||
        user?.role?.nombre ||
        user?.role ||
        roleLabelMap[user?.role_id] ||
        roleLabelMap[user?.roleId] ||
        'user'
    );
    const roleId = Number(user?.role_id || user?.roleId || roleIdMap[roleName] || 4);

    return {
        id: String(user?.id || user?._id || user?.user_id || ''),
        nombre: user?.nombre || user?.firstName || user?.name || '',
        apellidos: user?.apellidos || user?.lastName || '',
        email: user?.email || '',
        telefono: user?.telefono || user?.phone || '',
        sexo: user?.sexo || user?.gender || '',
        active: typeof user?.active === 'boolean' ? user.active : null,
        email_verified: typeof user?.email_verified === 'boolean' ? user.email_verified : null,
        createdAt: user?.createdAt || user?.created_at || null,
        updatedAt: user?.updatedAt || user?.updated_at || null,
        lastLoginAt: user?.lastLoginAt || user?.last_login_at || user?.ultimo_login_at || null,
        ultimos_logins: Array.isArray(user?.ultimos_logins) ? user.ultimos_logins : (Array.isArray(user?.last_logins) ? user.last_logins : []),
        ultimas_acciones_undoables: Array.isArray(user?.ultimas_acciones_undoables)
            ? user.ultimas_acciones_undoables
            : (Array.isArray(user?.ultimas_acciones) ? user.ultimas_acciones : []),
        tenant_id: getTenantIdFromUser(user),
        role_id: roleId,
        role_name: roleName,
        especialidad: user?.especialidad ?? null,
        cedula_profesional: user?.cedula_profesional ?? null,
        cedula_especialidad: user?.cedula_especialidad ?? null,
        cnmmt: user?.cnmmt ?? null,
        consultorio: user?.consultorio ?? null,
        biografia: user?.biografia ?? null,
        foto_perfil_url: normalizeUploadsPath(pickProfilePhotoRaw(user)) ?? pickProfilePhotoRaw(user) ?? null,
        firma_url: normalizeSignatureUploadsPath(pickProfileSignatureRaw(user)) ?? pickProfileSignatureRaw(user) ?? null,
        rol_nombre:
            user?.rol_nombre ||
            user?.role_name ||
            user?.role?.nombre ||
            (typeof user?.role === 'string' ? user.role : '') ||
            ''
    };
};

const normalizeHistoryEvent = event => ({
    id: String(event?.id || event?._id || event?.event_id || `${event?.action || 'event'}-${event?.occurred_at || Date.now()}`),
    occurred_at: event?.occurred_at || event?.occurredAt || event?.created_at || event?.createdAt || null,
    action: String(event?.action || event?.accion || event?.type || 'UNKNOWN'),
    description: String(event?.description || event?.message || event?.detalle || ''),
    actor_user_id: event?.actor_user_id || event?.actorUserId || event?.actor_id || null,
    actor_name: event?.actor_name || event?.actorName || event?.actor || null,
    ip: event?.ip || null,
    user_agent: event?.user_agent || event?.userAgent || null,
    metadata: event?.metadata || null,
    canUndo: Boolean(event?.canUndo)
});

const buildFallbackUsers = () => {
    const currentUser = authService.getUser();
    const seeded = [
        {
            id: String(currentUser?.id || 'u-1'),
            nombre: currentUser?.nombre || 'Administrador',
            apellidos: currentUser?.apellidos || 'Sistema',
            email: currentUser?.email || 'admin@mediworks.mx',
            role_id: Number(currentUser?.role_id || currentUser?.role || 1),
            role_name: String(currentUser?.role_name || currentUser?.rol_nombre || 'super_admin').toLowerCase()
        },
        {
            id: 'u-2',
            nombre: 'Laura',
            apellidos: 'Salinas',
            email: 'laura@mediworks.mx',
            role_id: 4,
            role_name: 'user'
        },
        {
            id: 'u-3',
            nombre: 'Carlos',
            apellidos: 'Mendez',
            email: 'carlos@mediworks.mx',
            role_id: 3,
            role_name: 'doctor'
        }
    ];

    return seeded.map(normalizeUser).filter(user => user.id);
};

const getStoredProfiles = () => {
    const raw = localStorage.getItem(PROFILES_STORAGE_KEY);
    if (!raw) {
        const seeded = buildFallbackUsers();
        localStorage.setItem(PROFILES_STORAGE_KEY, JSON.stringify(seeded));
        return seeded;
    }

    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map(normalizeUser) : buildFallbackUsers();
    } catch (error) {
        return buildFallbackUsers();
    }
};

const parseRows = data => {
    const rows = Array.isArray(data) ? data : (data?.data || data?.users || []);
    return Array.isArray(rows) ? rows.map(normalizeUser).filter(user => user.id) : [];
};

const normalizeMe = (payload, existingUser = null) => {
    const base = unwrapMeResponse(payload);
    const user = normalizeUser(base || {});
    let merged = {
        ...user,
        especialidad: base?.especialidad ?? '',
        cedula_profesional: base?.cedula_profesional ?? '',
        cedula_especialidad: base?.cedula_especialidad ?? '',
        cnmmt: base?.cnmmt ?? '',
        consultorio: base?.consultorio ?? '',
        biografia: base?.biografia ?? '',
        foto_perfil_url: normalizeUploadsPath(pickProfilePhotoRaw(base)) ?? pickProfilePhotoRaw(base) ?? null,
        firma_url: normalizeSignatureUploadsPath(pickProfileSignatureRaw(base)) ?? pickProfileSignatureRaw(base) ?? null
    };

    if (existingUser && !hasRoleInApiPayload(payload)) {
        merged = preserveRoleFields(merged, existingUser);
    }

    return merged;
};

export const profilesService = {
    async getMe() {
        try {
            const {data} = await profilesAPI.get('/usuarios/me');
            return normalizeMe(data);
        } catch (error) {
            throwApiError(error, 'No se pudo obtener la informacion del perfil.');
        }
    },

    async updateMe(payload) {
        try {
            const {data} = await profilesAPI.put('/usuarios/me', payload);
            const currentUser = authService.getUser() || {};
            const normalized = normalizeMe(data, currentUser);
            authService.updateStoredUser(normalized);
            return normalized;
        } catch (error) {
            throwApiError(error, 'No se pudo actualizar el perfil.');
        }
    },

    async getAll() {
        try {
            const {data} = await profilesAPI.get('/usuarios/medicos-usuarios');
            const rows = filterUsersByCurrentTenant(parseRows(data));
            if (rows.length) {
                return rows;
            }
        } catch (error) {
            // fall back to local storage below
        }

        return filterUsersByCurrentTenant(getStoredProfiles());
    },

    async getById(id) {
        try {
            const {data} = await profilesAPI.get(`/usuarios/${id}`);
            return normalizeUser(unwrapMeResponse(data));
        } catch (error) {
            const rows = getStoredProfiles();
            const row = rows.find(user => String(user.id) === String(id));
            if (row) {
                return normalizeUser(row);
            }

            throwApiError(error, 'Usuario no encontrado');
        }
    },

    async getHistoryByUser(id, options = {}) {
        const page = Number(options?.page || 1);
        const limit = Number(options?.limit || 10);

        const params = {page, limit};
        if (options?.action) params.action = options.action;
        if (options?.from) params.from = options.from;
        if (options?.to) params.to = options.to;

        try {
            const {data} = await profilesAPI.get(`/usuarios/${id}/historial`, {params});
            const payload = data?.data || data || {};
            const rawItems = Array.isArray(payload?.items)
                ? payload.items
                : (Array.isArray(payload?.rows) ? payload.rows : []);

            return {
                items: rawItems.map(normalizeHistoryEvent),
                page: Number(payload?.page || page),
                limit: Number(payload?.limit || limit),
                total: Number(payload?.total || rawItems.length)
            };
        } catch (error) {
            return {
                items: [],
                page,
                limit,
                total: 0
            };
        }
    },

    async updateRole(id, nextRole, currentUserPassword) {
        const normalizedRole = normalizeRoleName(nextRole);
        const roleNombre = roleNamePayloadMap[normalizedRole];

        if (!currentUserPassword?.trim()) {
            throw new Error('Debes ingresar tu contrasena para confirmar el cambio.');
        }

        if (!['user', 'doctor'].includes(normalizedRole)) {
            throw new Error('No esta permitido asignar roles Admin o SuperAdmin desde esta pantalla.');
        }

        if (!roleNombre) {
            throw new Error('Rol destino invalido.');
        }

        try {
            const {data} = await profilesAPI.patch(`/usuarios/${id}/rol`, {
                password: currentUserPassword,
                role_nombre: roleNombre
            });

            // Some backends return only role-related fields on PATCH.
            // Re-fetch full user details to preserve existing profile data in the UI.
            try {
                return await this.getById(id);
            } catch (readError) {
                return normalizeUser(data?.data || data);
            }
        } catch (error) {
            const message = error?.response?.data?.message || error?.response?.data?.error || '';
            if (message) {
                throw new Error(message);
            }
            throw new Error('No se pudo actualizar el rol.');
        }
    },

    async uploadPhoto(file) {
        const formData = new FormData();
        formData.append('photo', file);

        try {
            const {data} = await profilesAPI.put('/usuarios/me/photo', formData);
            const photoPath = extractProfilePhotoPathFromResponse(data);

            if (!photoPath) {
                throw new Error('El servidor no devolvio la URL de la foto.');
            }

            authService.updateStoredUser({foto_perfil_url: photoPath});
            await this.getPhotoDisplayBlob(photoPath);
            window.dispatchEvent(new CustomEvent('profile-photo-updated', {detail: {foto_perfil_url: photoPath}}));
            return photoPath;
        } catch (error) {
            throwApiError(error, 'No se pudo subir la foto de perfil.');
        }
    },

    async deletePhoto() {
        try {
            await profilesAPI.delete('/usuarios/me/photo');
            this.revokePhotoBlobCache();
            authService.updateStoredUser({foto_perfil_url: null});
            window.dispatchEvent(new CustomEvent('profile-photo-updated', {detail: {foto_perfil_url: null}}));
        } catch (error) {
            throwApiError(error, 'No se pudo eliminar la foto de perfil.');
        }
    },

    revokePhotoBlobCache() {
        if (cachedPhotoBlobUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(cachedPhotoBlobUrl);
        }

        cachedPhotoBlobUrl = null;
        cachedPhotoBlobPath = null;
    },

    /**
     * GET /usuarios/me/photo — fuente principal (Bearer + imagen).
     * Evita depender de nginx /uploads en producción.
     */
    async fetchPhotoBlobUrl() {
        try {
            const response = await profilesAPI.get('/usuarios/me/photo', {
                responseType: 'blob',
                headers: {Accept: 'image/*'},
                validateStatus: status => status === 200
            });

            const blob = response.data;

            if (!(blob instanceof Blob) || blob.size === 0) {
                return null;
            }

            if (blob.type && (blob.type.includes('json') || blob.type.includes('text'))) {
                return null;
            }

            return URL.createObjectURL(blob);
        } catch (error) {
            return null;
        }
    },

    /** Blob en caché por ruta; una sola petición para header + configuración. */
    async getPhotoDisplayBlob(path) {
        const normalizedPath = normalizeUploadsPath(path) || path;

        if (!normalizedPath) {
            this.revokePhotoBlobCache();
            return null;
        }

        if (cachedPhotoBlobPath === normalizedPath && cachedPhotoBlobUrl) {
            return cachedPhotoBlobUrl;
        }

        this.revokePhotoBlobCache();

        const blobUrl = await this.fetchPhotoBlobUrl();

        if (!blobUrl) {
            return null;
        }

        cachedPhotoBlobPath = normalizedPath;
        cachedPhotoBlobUrl = blobUrl;
        return blobUrl;
    },

    async uploadSignature(file) {
        const formData = new FormData();
        formData.append('signature', file);

        try {
            const {data} = await profilesAPI.put('/usuarios/me/signature', formData);
            const signaturePath = extractProfileSignaturePathFromResponse(data);

            if (!signaturePath) {
                throw new Error('El servidor no devolvio la URL de la firma.');
            }

            authService.updateStoredUser({firma_url: signaturePath});
            await this.getSignatureDisplayBlob(signaturePath);
            window.dispatchEvent(new CustomEvent('profile-signature-updated', {detail: {firma_url: signaturePath}}));
            return signaturePath;
        } catch (error) {
            throwApiError(error, 'No se pudo subir la firma.');
        }
    },

    async deleteSignature() {
        try {
            await profilesAPI.delete('/usuarios/me/signature');
            this.revokeSignatureBlobCache();
            authService.updateStoredUser({firma_url: null});
            window.dispatchEvent(new CustomEvent('profile-signature-updated', {detail: {firma_url: null}}));
        } catch (error) {
            throwApiError(error, 'No se pudo eliminar la firma.');
        }
    },

    revokeSignatureBlobCache() {
        if (cachedSignatureBlobUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(cachedSignatureBlobUrl);
        }

        cachedSignatureBlobUrl = null;
        cachedSignatureBlobPath = null;
    },

    async fetchSignatureBlobUrl() {
        try {
            const response = await profilesAPI.get('/usuarios/me/signature', {
                responseType: 'blob',
                headers: {Accept: 'image/*'},
                validateStatus: status => status === 200
            });

            const blob = response.data;

            if (!(blob instanceof Blob) || blob.size === 0) {
                return null;
            }

            if (blob.type && (blob.type.includes('json') || blob.type.includes('text'))) {
                return null;
            }

            return URL.createObjectURL(blob);
        } catch (error) {
            return null;
        }
    },

    async getSignatureDisplayBlob(path) {
        const normalizedPath = normalizeSignatureUploadsPath(path) || path;

        if (!normalizedPath) {
            this.revokeSignatureBlobCache();
            return null;
        }

        if (cachedSignatureBlobPath === normalizedPath && cachedSignatureBlobUrl) {
            return cachedSignatureBlobUrl;
        }

        this.revokeSignatureBlobCache();

        const blobUrl = await this.fetchSignatureBlobUrl();

        if (!blobUrl) {
            return null;
        }

        cachedSignatureBlobPath = normalizedPath;
        cachedSignatureBlobUrl = blobUrl;
        return blobUrl;
    }
};
